/**
 * The page tree with drag-and-drop. Native HTML5 DnD — no library. Dropping on a row's upper
 * third places the page before it, the lower third after it, the middle makes it a child.
 */
import { useMemo, useState } from "react";

import type { TreePage } from "../../api";

type TreeNode = TreePage & { children: TreeNode[] };

export type DropIntent = { targetId: string; placement: "before" | "after" | "inside" };

export function buildTree(pages: TreePage[]): TreeNode[] {
  const byId = new Map<string, TreeNode>(pages.map((p) => [p.id, { ...p, children: [] }]));
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parent_id ? byId.get(node.parent_id) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export function PageTree({
  pages,
  activePageId,
  onOpen,
  onDrop,
}: {
  pages: TreePage[];
  activePageId: string | null;
  onOpen: (id: string) => void;
  onDrop: (draggedId: string, intent: DropIntent) => void;
}) {
  const roots = useMemo(() => buildTree(pages), [pages]);
  const [dragged, setDragged] = useState<string | null>(null);
  const [hover, setHover] = useState<DropIntent | null>(null);

  const rows = (nodes: TreeNode[], depth: number): React.ReactNode =>
    nodes.map((node) => (
      // min-w-0 all the way down: grid items refuse to shrink below their content's
      // min-content width otherwise, and long titles push the whole column over the editor.
      <li key={node.id} className="min-w-0">
        <div
          draggable
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = "move";
            setDragged(node.id);
          }}
          onDragEnd={() => {
            setDragged(null);
            setHover(null);
          }}
          onDragOver={(event) => {
            if (!dragged || dragged === node.id) {
              return;
            }
            event.preventDefault();
            const rect = event.currentTarget.getBoundingClientRect();
            const y = (event.clientY - rect.top) / rect.height;
            const placement = y < 0.3 ? "before" : y > 0.7 ? "after" : "inside";
            setHover({ targetId: node.id, placement });
          }}
          onDrop={(event) => {
            event.preventDefault();
            if (dragged && hover && dragged !== node.id) {
              onDrop(dragged, hover);
            }
            setDragged(null);
            setHover(null);
          }}
          className={[
            "flex w-full items-center rounded-control text-body transition-[background] duration-control ease-nb",
            node.id === activePageId ? "bg-surface-1 shadow-card" : "hover:bg-surface-1",
            hover?.targetId === node.id && hover.placement === "inside"
              ? "outline outline-2 outline-wave"
              : "",
            hover?.targetId === node.id && hover.placement === "before"
              ? "border-t-2 border-t-drop"
              : "",
            hover?.targetId === node.id && hover.placement === "after"
              ? "border-b-2 border-b-drop"
              : "",
          ].join(" ")}
          style={{ paddingLeft: `calc(${depth} * var(--s5))` }}
        >
          <button
            type="button"
            onClick={() => onOpen(node.id)}
            className={`flex-1 truncate px-s4 py-s3 text-left ${
              node.id === activePageId ? "font-medium text-ink" : "text-muted"
            }`}
          >
            {node.title}
          </button>
        </div>
        {node.children.length > 0 && <ul className="min-w-0">{rows(node.children, depth + 1)}</ul>}
      </li>
    ));

  return (
    <ul aria-label="Sidträd" className="grid min-w-0 max-w-full gap-s1">
      {rows(roots, 0)}
    </ul>
  );
}
